import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataMutation, useDataProvider } from '@work4all/data';

import { Unit } from '@work4all/models/lib/Classes/Unit.entity';
import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EditTableColumns } from '../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { EditableEntityTable } from '../editable-entity-table/EditableEntityTable';

const UNIT_DATA: Unit = {
  id: null,
  name: null,
  minutes: null,
  order: null,
  unitEInvoice: null,
};

const newEntryMandatoryFields: (keyof Unit)[] = ['name'];

const req: DataRequest = {
  data: {
    id: null,
    order: null,
  } as Unit,
  entity: Entities.unit,
  sort: [
    {
      direction: SortDirection.ASCENDING,
      field: 'order',
    },
  ],
};

function move(input: Unit[], from: number, to: number) {
  let numberOfDeletedElm = 1;

  const elm = input.splice(from, numberOfDeletedElm)[0];

  numberOfDeletedElm = 0;

  input.splice(to, numberOfDeletedElm, elm);
}

export const UnitTable = () => {
  const { t } = useTranslation();

  const cols = useMemo<EditTableColumns<Unit>[]>(
    () => [
      {
        Header: t('COMMON.NAME'),
        accessor: 'name',
        width: 150,
        type: 'text',
      },
      {
        Header: t('COMMON.MINUTE', { count: 2 }),
        accessor: 'minutes',
        width: 80,
        type: 'number',
        numberCellProps: {
          showThousandsSeparator: false,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      },
      {
        Header: t('COMMON.UNIT.UNIT_E_INVOICE'),
        accessor: 'unitEInvoice',
        width: 200,
        type: 'text',
      },
    ],
    [t]
  );

  const [mutate] = useDataMutation({
    entity: Entities.unit,
    mutationType: EMode.upsert,
    responseData: { id: null, order: null },
  });

  const { data } = useDataProvider<Unit>(req);
  const sortedData = useMemo(() => sortBy(data, 'order'), [data]);
  const handleMovePosition = useCallback(
    (localId: number, newIdx: number) => {
      const newOrder = [...sortedData];
      const oldIdx = sortedData.findIndex((x) => x.id === localId);

      move(newOrder, oldIdx, newIdx);

      newOrder.forEach((item, idx) => {
        mutate({ id: item.id, order: idx });
      });
    },
    [mutate, sortedData]
  );

  const handleTransformBeforeUpdate = useCallback(
    (unit: Unit) => {
      if (unit.id === undefined && unit.order === undefined) {
        unit.order = sortedData.length;
      }
      return unit;
    },
    [sortedData.length]
  );

  return (
    <EditableEntityTable<Unit>
      columns={cols}
      dataFields={UNIT_DATA}
      entity={Entities.unit}
      idType="number"
      sortByField="order"
      newEntryMandatoryFields={newEntryMandatoryFields}
      onMovePosition={handleMovePosition}
      transformBeforeUpdate={handleTransformBeforeUpdate}
      enableDelete
      dragHandlerVisible={(val) => {
        return val.id !== -1;
      }}
      dragHandlerClickable={() => false}
    />
  );
};
